/**
 * Email copy and HTML aligned with docs/webplatform Next.js API routes (Resend).
 * Dynamic values are escaped for safe HTML interpolation.
 */

import { getPublicAppOrigin } from '@/config/env.ts'

export function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

/** Public app origin for email templates (uses {@link getPublicAppOrigin}). */
export function getAppUrl(): string {
  return getPublicAppOrigin()
}

export function vaultDetailPath(vaultId: number | bigint): string {
  return `/dashboard/vaults/${vaultId.toString()}`
}

export function formatDurationSeconds(seconds: number): string {
  const days = Math.floor(seconds / 86400)
  const hours = Math.floor((seconds % 86400) / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  if (days > 0) return `${days} day${days > 1 ? 's' : ''}`
  if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''}`
  return `${minutes} minute${minutes > 1 ? 's' : ''}`
}

export function subscriptionPlanName(tier: number): string {
  if (tier === 1) return '12 Month Subscription'
  if (tier === 2) return '1 Month Subscription'
  return 'Subscription Plan'
}

export function formatExpiryDateFromUnixSeconds(expiry: bigint): string {
  const ms = Number(expiry) * 1000
  if (!Number.isFinite(ms)) return 'N/A'
  return new Date(ms).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

// --- A: notify-vault-created (benefactor) ---

export function vaultCreatedBenefactorEmail(params: {
  appUrl: string
  vaultId: number
  vaultName: string
  countdownMinutes: number
  beneficiaryWalletAddress: string
  benefactorName: string
}): { subject: string; text: string; html: string } {
  const {
    appUrl,
    vaultId,
    vaultName,
    countdownMinutes,
    beneficiaryWalletAddress,
    benefactorName,
  } = params
  const e = escapeHtml
  const vn = e(vaultName)
  const bn = e(benefactorName)
  const bw = e(beneficiaryWalletAddress)
  const path = vaultDetailPath(vaultId)

  const subject = `Vault #${vaultId} Created - BitsLegacy`

  const text = `Vault #${vaultId} has been created successfully!

Vault Details:
- Vault ID: #${vaultId}
- Vault Name: ${vaultName}
- Beneficiary: ${beneficiaryWalletAddress}
- Countdown Duration: ${countdownMinutes} minutes

You can view and manage this vault in your dashboard: ${appUrl}${path}`

  const html = `
        <!DOCTYPE html>
        <html>
          <head>
            <meta charset="utf-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <title>Vault Created - BitsLegacy</title>
          </head>
          <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px;">
            <div style="background: linear-gradient(135deg, #9810FA 0%, #C27AFF 100%); padding: 30px; text-align: center; border-radius: 10px 10px 0 0;">
              <h1 style="color: white; margin: 0;">BitsLegacy</h1>
            </div>
            
            <div style="background: #f9f9f9; padding: 30px; border-radius: 0 0 10px 10px; border: 1px solid #e0e0e0;">
              <h2 style="color: #9810FA; margin-top: 0;">✅ Vault Created Successfully!</h2>
              
              <p>Hello ${bn},</p>
              
              <p>Your vault has been created successfully on BitsLegacy.</p>
              
              <div style="background: white; padding: 20px; border-radius: 5px; border-left: 4px solid #9810FA; margin: 20px 0;">
                <p style="margin: 5px 0;"><strong>Vault ID:</strong> #${vaultId}</p>
                <p style="margin: 5px 0;"><strong>Vault Name:</strong> ${vn}</p>
                <p style="margin: 5px 0;"><strong>Beneficiary:</strong> <code style="background: #f0f0f0; padding: 2px 6px; border-radius: 3px; font-size: 12px;">${bw}</code></p>
                <p style="margin: 5px 0;"><strong>Countdown Duration:</strong> ${countdownMinutes} minutes</p>
              </div>
              
              <div style="text-align: center; margin: 30px 0;">
                <a href="${e(appUrl)}${path}" style="display: inline-block; background: #9810FA; color: white; padding: 12px 30px; text-decoration: none; border-radius: 5px; font-weight: bold;">View Vault</a>
              </div>
            </div>
          </body>
        </html>
      `

  return { subject, text, html }
}

// --- A: notify-vault-created (beneficiary) ---

export function vaultCreatedBeneficiaryEmail(params: {
  appUrl: string
  vaultId: number
  beneficiaryWalletAddress: string
}): { subject: string; text: string; html: string } {
  const { appUrl, vaultId, beneficiaryWalletAddress } = params
  const e = escapeHtml
  const bw = e(beneficiaryWalletAddress)

  const subject = `A Vault Has Been Created For You - BitsLegacy`

  const text = `🔐 A vault has been created for you on BitsLegacy!

Vault ID: #${vaultId}
Your Wallet: ${beneficiaryWalletAddress}

Connect your wallet and view vaults: ${appUrl}

You will be able to access this vault after the countdown timer expires.`

  const html = `
        <!DOCTYPE html>
        <html>
          <head>
            <meta charset="utf-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <title>Vault Created For You - BitsLegacy</title>
          </head>
          <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px;">
            <div style="background: linear-gradient(135deg, #9810FA 0%, #C27AFF 100%); padding: 30px; text-align: center; border-radius: 10px 10px 0 0;">
              <h1 style="color: white; margin: 0;">BitsLegacy</h1>
            </div>
            
            <div style="background: #f9f9f9; padding: 30px; border-radius: 0 0 10px 10px; border: 1px solid #e0e0e0;">
              <h2 style="color: #9810FA; margin-top: 0;">🔐 A Vault Has Been Created For You</h2>
              
              <p>Hello,</p>
              
              <p>You have been added as a beneficiary to a vault on BitsLegacy.</p>
              
              <div style="background: white; padding: 20px; border-radius: 5px; border-left: 4px solid #9810FA; margin: 20px 0;">
                <p style="margin: 5px 0;"><strong>Vault ID:</strong> #${vaultId}</p>
                <p style="margin: 5px 0;"><strong>Your Wallet Address:</strong> <code style="background: #f0f0f0; padding: 2px 6px; border-radius: 3px; font-size: 12px;">${bw}</code></p>
              </div>
              
              <p><strong>What happens next:</strong></p>
              <ul style="padding-left: 20px;">
                <li>Connect your wallet at ${e(appUrl)}</li>
                <li>View your assigned vaults in the dashboard</li>
                <li>Start the countdown timer when ready</li>
                <li>Access the vault after the countdown expires</li>
              </ul>
              
              <div style="text-align: center; margin: 30px 0;">
                <a href="${e(appUrl)}" style="display: inline-block; background: #9810FA; color: white; padding: 12px 30px; text-decoration: none; border-radius: 5px; font-weight: bold;">Connect Wallet & View Vaults</a>
              </div>
            </div>
          </body>
        </html>
      `

  return { subject, text, html }
}

// --- B: notify-countdown-started (benefactor email only in old API for this path) ---

export function countdownStartedBenefactorEmail(params: {
  appUrl: string
  vaultId: number
  beneficiaryWalletAddress: string
  benefactorName: string
  durationText: string
}): { subject: string; text: string; html: string } {
  const {
    appUrl,
    vaultId,
    beneficiaryWalletAddress,
    benefactorName,
    durationText,
  } = params
  const e = escapeHtml
  const path = vaultDetailPath(vaultId)
  const subject = `Countdown Timer Started - Vault #${vaultId} - BitsLegacy`

  const text = `
Countdown Timer Started - Vault #${vaultId} - BitsLegacy

Hello ${benefactorName},

The countdown timer for Vault #${vaultId} has been activated by the beneficiary.

Vault Details:
- Vault ID: #${vaultId}
- Beneficiary: ${beneficiaryWalletAddress}
- Countdown Duration: ${durationText}

What this means:
- The beneficiary has started the countdown timer
- The vault will be accessible after the countdown expires
- You can monitor the countdown status in your dashboard

View Vault: ${appUrl}${path}

You will receive another notification when the countdown expires and the vault is accessed.

© ${new Date().getFullYear()} BitsLegacy. All rights reserved.
            `.trim()

  const html = `
              <!DOCTYPE html>
              <html>
                <head>
                  <meta charset="utf-8">
                  <meta name="viewport" content="width=device-width, initial-scale=1.0">
                  <title>Countdown Started - BitsLegacy</title>
                </head>
                <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px;">
                  <div style="background: linear-gradient(135deg, #9810FA 0%, #C27AFF 100%); padding: 30px; text-align: center; border-radius: 10px 10px 0 0;">
                    <h1 style="color: white; margin: 0;">BitsLegacy</h1>
                  </div>
                  
                  <div style="background: #f9f9f9; padding: 30px; border-radius: 0 0 10px 10px; border: 1px solid #e0e0e0;">
                    <h2 style="color: #9810FA; margin-top: 0;">⏱️ Countdown Timer Started</h2>
                    
                    <p>Hello ${e(benefactorName)},</p>
                    
                    <p>The countdown timer for <strong>Vault #${vaultId}</strong> has been activated by the beneficiary.</p>
                    
                    <div style="background: white; padding: 20px; border-radius: 5px; border-left: 4px solid #9810FA; margin: 20px 0;">
                      <p style="margin: 5px 0;"><strong>Vault ID:</strong> #${vaultId}</p>
                      <p style="margin: 5px 0;"><strong>Beneficiary:</strong> <code style="background: #f0f0f0; padding: 2px 6px; border-radius: 3px;">${e(beneficiaryWalletAddress)}</code></p>
                      <p style="margin: 5px 0;"><strong>Countdown Duration:</strong> ${e(durationText)}</p>
                    </div>
                    
                    <p><strong>What this means:</strong></p>
                    <ul style="padding-left: 20px;">
                      <li>The beneficiary has started the countdown timer</li>
                      <li>The vault will be accessible after the countdown expires</li>
                      <li>You can monitor the countdown status in your dashboard</li>
                    </ul>
                    
                    <div style="text-align: center; margin: 30px 0;">
                      <a href="${e(appUrl)}${path}" style="display: inline-block; background: #9810FA; color: white; padding: 12px 30px; text-decoration: none; border-radius: 5px; font-weight: bold;">View Vault Details</a>
                    </div>
                    
                    <p style="color: #666; font-size: 14px; margin-top: 30px; padding-top: 20px; border-top: 1px solid #e0e0e0;">
                      You will receive another notification when the countdown expires and the vault is accessed.
                    </p>
                  </div>
                </body>
              </html>
            `

  return { subject, text, html }
}

export function countdownStartedBeneficiaryEmail(params: {
  appUrl: string
  vaultId: number
  beneficiaryWalletAddress: string
  durationText: string
}): { subject: string; text: string; html: string } {
  const { appUrl, vaultId, beneficiaryWalletAddress, durationText } = params
  const e = escapeHtml
  const subject = `Countdown Timer Started - Vault #${vaultId} - BitsLegacy`

  const text = `
Countdown Timer Started - Vault #${vaultId} - BitsLegacy

Hello,

You have successfully started the countdown timer for Vault #${vaultId}.

Vault Details:
- Vault ID: #${vaultId}
- Your Wallet Address: ${beneficiaryWalletAddress}
- Countdown Duration: ${durationText}

What happens next:
- The countdown timer is now active and counting down
- You will be able to decrypt and access the vault when the countdown expires
- You can monitor the countdown progress in your dashboard

Connect Wallet & View Vaults: ${appUrl}

Note: Make sure you have access to your wallet address (${beneficiaryWalletAddress}). You will need it to connect and decrypt the vault when the countdown expires.

© ${new Date().getFullYear()} BitsLegacy. All rights reserved.
            `.trim()

  const html = `
              <!DOCTYPE html>
              <html>
                <head>
                  <meta charset="utf-8">
                  <meta name="viewport" content="width=device-width, initial-scale=1.0">
                  <title>Countdown Started - BitsLegacy</title>
                </head>
                <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px;">
                  <div style="background: linear-gradient(135deg, #9810FA 0%, #C27AFF 100%); padding: 30px; text-align: center; border-radius: 10px 10px 0 0;">
                    <h1 style="color: white; margin: 0;">BitsLegacy</h1>
                  </div>
                  
                  <div style="background: #f9f9f9; padding: 30px; border-radius: 0 0 10px 10px; border: 1px solid #e0e0e0;">
                    <h2 style="color: #9810FA; margin-top: 0;">⏱️ Countdown Timer Started</h2>
                    
                    <p>Hello,</p>
                    
                    <p>You have successfully started the countdown timer for <strong>Vault #${vaultId}</strong>.</p>
                    
                    <div style="background: white; padding: 20px; border-radius: 5px; border-left: 4px solid #9810FA; margin: 20px 0;">
                      <p style="margin: 5px 0;"><strong>Vault ID:</strong> #${vaultId}</p>
                      <p style="margin: 5px 0;"><strong>Your Wallet Address:</strong> <code style="background: #f0f0f0; padding: 2px 6px; border-radius: 3px;">${e(beneficiaryWalletAddress)}</code></p>
                      <p style="margin: 5px 0;"><strong>Countdown Duration:</strong> ${e(durationText)}</p>
                    </div>
                    
                    <p><strong>What happens next:</strong></p>
                    <ul style="padding-left: 20px;">
                      <li>The countdown timer is now active and counting down</li>
                      <li>You will be able to decrypt and access the vault when the countdown expires</li>
                      <li>You can monitor the countdown progress in your dashboard</li>
                    </ul>
                    
                    <div style="text-align: center; margin: 30px 0;">
                      <a href="${e(appUrl)}" style="display: inline-block; background: #9810FA; color: white; padding: 12px 30px; text-decoration: none; border-radius: 5px; font-weight: bold;">Connect Wallet & View Vaults</a>
                    </div>
                    
                    <p style="color: #666; font-size: 14px; margin-top: 30px; padding-top: 20px; border-top: 1px solid #e0e0e0;">
                      <strong>Note:</strong> Make sure you have access to your wallet address (${e(beneficiaryWalletAddress)}). You will need it to connect and decrypt the vault when the countdown expires.
                    </p>
                  </div>
                </body>
              </html>
            `

  return { subject, text, html }
}

// --- C: notify-beneficiary (manual / resend) ---

export function notifyBeneficiaryVaultEmail(params: {
  appUrl: string
  vaultId: number
  beneficiaryWalletAddress: string
}): { subject: string; text: string; html: string } {
  const { appUrl, vaultId, beneficiaryWalletAddress } = params
  const e = escapeHtml
  const bw = e(beneficiaryWalletAddress)
  const subject = `A Vault Has Been Created For You - BitsLegacy`

  const text = `
A Vault Has Been Created For You - BitsLegacy

Hello,

A secure vault has been created for you on BitsLegacy. This vault contains encrypted information that you will be able to access after the countdown period expires.

Vault Details:
- Vault ID: #${vaultId}
- Your Wallet Address: ${beneficiaryWalletAddress}

What you need to do:
1. Register or log in to BitsLegacy using your wallet address: ${beneficiaryWalletAddress}
2. Wait for the countdown timer to expire
3. Access your vault from the dashboard

Connect Wallet & View Vaults: ${appUrl}

Important: Make sure you have access to the wallet address listed above. You will need it to decrypt and access the vault contents.

© ${new Date().getFullYear()} BitsLegacy. All rights reserved.
        `.trim()

  const html = `
          <!DOCTYPE html>
          <html>
            <head>
              <meta charset="utf-8">
              <meta name="viewport" content="width=device-width, initial-scale=1.0">
              <title>Vault Created - BitsLegacy</title>
            </head>
            <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px;">
              <div style="background: linear-gradient(135deg, #9810FA 0%, #C27AFF 100%); padding: 30px; text-align: center; border-radius: 10px 10px 0 0;">
                <h1 style="color: white; margin: 0;">BitsLegacy</h1>
              </div>
              
              <div style="background: #f9f9f9; padding: 30px; border-radius: 0 0 10px 10px; border: 1px solid #e0e0e0;">
                <h2 style="color: #9810FA; margin-top: 0;">A Vault Has Been Created For You</h2>
                
                <p>Hello,</p>
                
                <p>A secure vault has been created for you on BitsLegacy. This vault contains encrypted information that you will be able to access after the countdown period expires.</p>
                
                <div style="background: white; padding: 20px; border-radius: 5px; border-left: 4px solid #9810FA; margin: 20px 0;">
                  <p style="margin: 5px 0;"><strong>Vault ID:</strong> #${vaultId}</p>
                  <p style="margin: 5px 0;"><strong>Your Wallet Address:</strong> <code style="background: #f0f0f0; padding: 2px 6px; border-radius: 3px;">${bw}</code></p>
                </div>
                
                <p><strong>What you need to do:</strong></p>
                <ol style="padding-left: 20px;">
                  <li>Register or log in to BitsLegacy using your wallet address: <code style="background: #f0f0f0; padding: 2px 6px; border-radius: 3px;">${bw}</code></li>
                  <li>Wait for the countdown timer to expire</li>
                  <li>Access your vault from the dashboard</li>
                </ol>
                
                <div style="text-align: center; margin: 30px 0;">
                  <a href="${e(appUrl)}" style="display: inline-block; background: #9810FA; color: white; padding: 12px 30px; text-decoration: none; border-radius: 5px; font-weight: bold;">Connect Wallet & View Vaults</a>
                </div>
                
                <p style="color: #666; font-size: 14px; margin-top: 30px; padding-top: 20px; border-top: 1px solid #e0e0e0;">
                  <strong>Important:</strong> Make sure you have access to the wallet address listed above. You will need it to decrypt and access the vault contents.
                </p>
              </div>
            </body>
          </html>
        `

  return { subject, text, html }
}

// --- D: notify-countdown-reset ---

export function countdownResetBenefactorEmail(params: {
  appUrl: string
  vaultId: number
  benefactorName: string
}): { subject: string; text: string; html: string } {
  const { appUrl, vaultId, benefactorName } = params
  const e = escapeHtml
  const path = vaultDetailPath(vaultId)
  const subject = `Countdown Reset - Vault #${vaultId} - BitsLegacy`

  const text = `✅ Countdown Reset - Vault #${vaultId}

You have successfully reset the countdown timer for Vault #${vaultId}.

The countdown has been stopped and the beneficiary will need to start a new countdown to access the vault.

View Vault: ${appUrl}${path}`

  const html = `
        <!DOCTYPE html>
        <html>
          <head>
            <meta charset="utf-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <title>Countdown Reset - BitsLegacy</title>
          </head>
          <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px;">
            <div style="background: linear-gradient(135deg, #9810FA 0%, #C27AFF 100%); padding: 30px; text-align: center; border-radius: 10px 10px 0 0;">
              <h1 style="color: white; margin: 0;">BitsLegacy</h1>
            </div>
            
            <div style="background: #f9f9f9; padding: 30px; border-radius: 0 0 10px 10px; border: 1px solid #e0e0e0;">
              <h2 style="color: #9810FA; margin-top: 0;">✅ Countdown Reset</h2>
              
              <p>Hello ${e(benefactorName)},</p>
              
              <p>You have successfully reset the countdown timer for Vault #${vaultId}.</p>
              
              <div style="background: white; padding: 20px; border-radius: 5px; border-left: 4px solid #9810FA; margin: 20px 0;">
                <p style="margin: 5px 0;"><strong>Vault ID:</strong> #${vaultId}</p>
                <p style="margin: 5px 0;"><strong>Status:</strong> Countdown stopped</p>
              </div>
              
              <p>The beneficiary will need to start a new countdown to access the vault.</p>
              
              <div style="text-align: center; margin: 30px 0;">
                <a href="${e(appUrl)}${path}" style="display: inline-block; background: #9810FA; color: white; padding: 12px 30px; text-decoration: none; border-radius: 5px; font-weight: bold;">View Vault</a>
              </div>
            </div>
          </body>
        </html>
      `

  return { subject, text, html }
}

export function countdownResetBeneficiaryEmail(params: {
  appUrl: string
  vaultId: number
}): { subject: string; text: string; html: string } {
  const { appUrl, vaultId } = params
  const e = escapeHtml
  const path = vaultDetailPath(vaultId)
  const subject = `Countdown Reset - Vault #${vaultId} - BitsLegacy`

  const text = `⚠️ Countdown Reset - Vault #${vaultId}

The countdown timer for Vault #${vaultId} has been reset by the benefactor.

The countdown has been stopped. You will need to start a new countdown to access the vault.

View Vault: ${appUrl}${path}`

  const html = `
        <!DOCTYPE html>
        <html>
          <head>
            <meta charset="utf-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <title>Countdown Reset - BitsLegacy</title>
          </head>
          <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px;">
            <div style="background: linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%); padding: 30px; text-align: center; border-radius: 10px 10px 0 0;">
              <h1 style="color: white; margin: 0;">BitsLegacy</h1>
            </div>
            
            <div style="background: #f9f9f9; padding: 30px; border-radius: 0 0 10px 10px; border: 1px solid #e0e0e0;">
              <h2 style="color: #f59e0b; margin-top: 0;">⚠️ Countdown Reset</h2>
              
              <p>Hello,</p>
              
              <p>The countdown timer for Vault #${vaultId} has been reset by the benefactor.</p>
              
              <div style="background: white; padding: 20px; border-radius: 5px; border-left: 4px solid #f59e0b; margin: 20px 0;">
                <p style="margin: 5px 0;"><strong>Vault ID:</strong> #${vaultId}</p>
                <p style="margin: 5px 0;"><strong>Status:</strong> Countdown stopped</p>
              </div>
              
              <p>You will need to start a new countdown to access the vault.</p>
              
              <div style="text-align: center; margin: 30px 0;">
                <a href="${e(appUrl)}${path}" style="display: inline-block; background: #f59e0b; color: white; padding: 12px 30px; text-decoration: none; border-radius: 5px; font-weight: bold;">View Vault</a>
              </div>
            </div>
          </body>
        </html>
      `

  return { subject, text, html }
}

// --- E: notify-countdown-reminder (benefactor) ---

export function countdownReminderBenefactorEmail(params: {
  appUrl: string
  vaultId: number
  benefactorName: string
  timeText: string
}): { subject: string; text: string; html: string } {
  const { appUrl, vaultId, benefactorName, timeText } = params
  const e = escapeHtml
  const path = vaultDetailPath(vaultId)
  const subject = `Countdown Reminder - Vault #${vaultId} - BitsLegacy`

  const text = `⏰ Countdown Reminder - Vault #${vaultId}

Only ${timeText} remaining before the countdown expires for Vault #${vaultId}.

If you want to prevent the beneficiary from accessing the vault, you should reset the countdown now.

View Vault: ${appUrl}${path}`

  const html = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Countdown Reminder - BitsLegacy</title>
        </head>
        <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px;">
          <div style="background: linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%); padding: 30px; text-align: center; border-radius: 10px 10px 0 0;">
            <h1 style="color: white; margin: 0;">BitsLegacy</h1>
          </div>
          
          <div style="background: #f9f9f9; padding: 30px; border-radius: 0 0 10px 10px; border: 1px solid #e0e0e0;">
            <h2 style="color: #f59e0b; margin-top: 0;">⏰ Countdown Reminder</h2>
            
            <p>Hello ${e(benefactorName)},</p>
            
            <p>Only <strong>${e(timeText)}</strong> remaining before the countdown expires for Vault #${vaultId}.</p>
            
            <div style="background: white; padding: 20px; border-radius: 5px; border-left: 4px solid #f59e0b; margin: 20px 0;">
              <p style="margin: 5px 0;"><strong>Vault ID:</strong> #${vaultId}</p>
              <p style="margin: 5px 0;"><strong>Time Remaining:</strong> ${e(timeText)}</p>
            </div>
            
            <p><strong>Important:</strong> If you want to prevent the beneficiary from accessing the vault, you should reset the countdown now.</p>
            
            <div style="text-align: center; margin: 30px 0;">
              <a href="${e(appUrl)}${path}" style="display: inline-block; background: #f59e0b; color: white; padding: 12px 30px; text-decoration: none; border-radius: 5px; font-weight: bold;">View Vault</a>
            </div>
          </div>
        </body>
      </html>
    `

  return { subject, text, html }
}

// --- F: notify-countdown-expired (beneficiary) ---

export function countdownExpiredBeneficiaryEmail(params: {
  appUrl: string
  vaultId: number
  beneficiaryWalletAddress: string
}): { subject: string; text: string; html: string } {
  const { appUrl, vaultId, beneficiaryWalletAddress } = params
  const e = escapeHtml
  const path = vaultDetailPath(vaultId)
  const subject = `Countdown Expired - Vault #${vaultId} Ready - BitsLegacy`

  const text = `🔓 Countdown Expired - Vault #${vaultId} is Ready!

The countdown timer for Vault #${vaultId} has expired. You can now decrypt and access the vault contents.

Vault ID: #${vaultId}
Your Wallet: ${beneficiaryWalletAddress}

Go to your dashboard to decrypt: ${appUrl}${path}`

  const html = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Countdown Expired - BitsLegacy</title>
        </head>
        <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px;">
          <div style="background: linear-gradient(135deg, #10b981 0%, #34d399 100%); padding: 30px; text-align: center; border-radius: 10px 10px 0 0;">
            <h1 style="color: white; margin: 0;">BitsLegacy</h1>
          </div>
          
          <div style="background: #f9f9f9; padding: 30px; border-radius: 0 0 10px 10px; border: 1px solid #e0e0e0;">
            <h2 style="color: #10b981; margin-top: 0;">🔓 Countdown Expired - Vault Ready!</h2>
            
            <p>Hello,</p>
            
            <p>The countdown timer for Vault #${vaultId} has expired. You can now decrypt and access the vault contents.</p>
            
            <div style="background: white; padding: 20px; border-radius: 5px; border-left: 4px solid #10b981; margin: 20px 0;">
              <p style="margin: 5px 0;"><strong>Vault ID:</strong> #${vaultId}</p>
              <p style="margin: 5px 0;"><strong>Your Wallet:</strong> <code style="background: #f0f0f0; padding: 2px 6px; border-radius: 3px; font-size: 12px;">${e(beneficiaryWalletAddress)}</code></p>
            </div>
            
            <div style="text-align: center; margin: 30px 0;">
              <a href="${e(appUrl)}${path}" style="display: inline-block; background: #10b981; color: white; padding: 12px 30px; text-decoration: none; border-radius: 5px; font-weight: bold;">Decrypt Vault</a>
            </div>
          </div>
        </body>
      </html>
    `

  return { subject, text, html }
}

// --- G: subscription verify confirmation ---

export function subscriptionConfirmedEmail(params: {
  appUrl: string
  userName: string
  planName: string
  expiryDate: string
  transactionHash: string
}): { subject: string; text: string; html: string } {
  const { appUrl, userName, planName, expiryDate, transactionHash } = params
  const e = escapeHtml
  const txShort = `${transactionHash.slice(0, 10)}...${transactionHash.slice(-8)}`
  const subject = `Subscription Confirmed - BitsLegacy`

  const text = `✅ Subscription Confirmed - BitsLegacy

Hello ${userName},

Thank you for subscribing to BitsLegacy! Your subscription has been successfully activated and verified.

Subscription Details:
- Plan: ${planName}
- Status: Active
- Expires: ${expiryDate}
- Transaction: ${txShort}

What you can do now:
- Create and manage vaults
- Set up beneficiaries for your vaults
- Monitor countdown timers
- Access all premium features

Go to Dashboard: ${appUrl}/dashboard

Thank you for choosing BitsLegacy!`

  const html = `
              <!DOCTYPE html>
              <html>
                <head>
                  <meta charset="utf-8">
                  <meta name="viewport" content="width=device-width, initial-scale=1.0">
                  <title>Subscription Confirmed - BitsLegacy</title>
                </head>
                <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px;">
                  <div style="background: linear-gradient(135deg, #9810FA 0%, #C27AFF 100%); padding: 30px; text-align: center; border-radius: 10px 10px 0 0;">
                    <h1 style="color: white; margin: 0;">BitsLegacy</h1>
                  </div>
                  
                  <div style="background: #f9f9f9; padding: 30px; border-radius: 0 0 10px 10px; border: 1px solid #e0e0e0;">
                    <h2 style="color: #9810FA; margin-top: 0;">✅ Subscription Confirmed!</h2>
                    
                    <p>Hello ${e(userName)},</p>
                    
                    <p>Thank you for subscribing to BitsLegacy! Your subscription has been successfully activated and verified.</p>
                    
                    <div style="background: white; padding: 20px; border-radius: 5px; border-left: 4px solid #9810FA; margin: 20px 0;">
                      <p style="margin: 5px 0;"><strong>Plan:</strong> ${e(planName)}</p>
                      <p style="margin: 5px 0;"><strong>Status:</strong> <span style="color: #10b981; font-weight: bold;">Active</span></p>
                      <p style="margin: 5px 0;"><strong>Expires:</strong> ${e(expiryDate)}</p>
                      <p style="margin: 5px 0;"><strong>Transaction:</strong> <code style="background: #f0f0f0; padding: 2px 6px; border-radius: 3px; font-size: 12px;">${e(txShort)}</code></p>
                    </div>
                    
                    <p><strong>What you can do now:</strong></p>
                    <ul style="padding-left: 20px;">
                      <li>Create and manage vaults</li>
                      <li>Set up beneficiaries for your vaults</li>
                      <li>Monitor countdown timers</li>
                      <li>Access all premium features</li>
                    </ul>
                    
                    <div style="text-align: center; margin: 30px 0;">
                      <a href="${e(appUrl)}/dashboard" style="display: inline-block; background: #9810FA; color: white; padding: 12px 30px; text-decoration: none; border-radius: 5px; font-weight: bold;">Go to Dashboard</a>
                    </div>
                    
                    <p style="color: #666; font-size: 14px; margin-top: 30px; padding-top: 20px; border-top: 1px solid #e0e0e0;">
                      If you have any questions or need assistance, please don't hesitate to contact our support team.
                    </p>
                    
                    <p style="color: #666; font-size: 14px; margin-top: 10px;">
                      Thank you for choosing BitsLegacy!
                    </p>
                  </div>
                </body>
              </html>
            `

  return { subject, text, html }
}

// --- H: subscription expiring ---

export function subscriptionExpiringEmail(params: {
  appUrl: string
  userName: string
  planName: string
  expiryDate: string
}): { subject: string; text: string; html: string } {
  const { appUrl, userName, planName, expiryDate } = params
  const e = escapeHtml
  const subject = `Subscription Expiring Soon - BitsLegacy`

  const text = `⏰ Subscription Expiring Soon - BitsLegacy

Hello ${userName},

Your BitsLegacy subscription is expiring in 1 month.

Subscription Details:
- Plan: ${planName}
- Expires: ${expiryDate}

To continue using BitsLegacy and avoid service interruption, please renew your subscription.

Renew Subscription: ${appUrl}/dashboard/subscribe`

  const html = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Subscription Expiring Soon - BitsLegacy</title>
        </head>
        <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px;">
          <div style="background: linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%); padding: 30px; text-align: center; border-radius: 10px 10px 0 0;">
            <h1 style="color: white; margin: 0;">BitsLegacy</h1>
          </div>
          
          <div style="background: #f9f9f9; padding: 30px; border-radius: 0 0 10px 10px; border: 1px solid #e0e0e0;">
            <h2 style="color: #f59e0b; margin-top: 0;">⏰ Subscription Expiring Soon</h2>
            
            <p>Hello ${e(userName)},</p>
            
            <p>Your BitsLegacy subscription is expiring in <strong>1 month</strong>.</p>
            
            <div style="background: white; padding: 20px; border-radius: 5px; border-left: 4px solid #f59e0b; margin: 20px 0;">
              <p style="margin: 5px 0;"><strong>Plan:</strong> ${e(planName)}</p>
              <p style="margin: 5px 0;"><strong>Expires:</strong> ${e(expiryDate)}</p>
            </div>
            
            <p><strong>Important:</strong> To continue using BitsLegacy and avoid service interruption, please renew your subscription.</p>
            
            <div style="text-align: center; margin: 30px 0;">
              <a href="${e(appUrl)}/dashboard/subscribe" style="display: inline-block; background: #f59e0b; color: white; padding: 12px 30px; text-decoration: none; border-radius: 5px; font-weight: bold;">Renew Subscription</a>
            </div>
          </div>
        </body>
      </html>
    `

  return { subject, text, html }
}

// --- I: subscription expired ---

export function subscriptionExpiredEmail(params: {
  appUrl: string
  userName: string
  planName: string
}): { subject: string; text: string; html: string } {
  const { appUrl, userName, planName } = params
  const e = escapeHtml
  const subject = `Subscription Expired - BitsLegacy`

  const text = `⚠️ Subscription Expired - BitsLegacy

Hello ${userName},

Your BitsLegacy subscription has expired.

Subscription Details:
- Plan: ${planName}
- Status: Expired

To continue using BitsLegacy, please renew your subscription. You will not be able to create new vaults until your subscription is renewed.

Renew Subscription: ${appUrl}/dashboard/subscribe`

  const html = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Subscription Expired - BitsLegacy</title>
        </head>
        <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px;">
          <div style="background: linear-gradient(135deg, #ef4444 0%, #f87171 100%); padding: 30px; text-align: center; border-radius: 10px 10px 0 0;">
            <h1 style="color: white; margin: 0;">BitsLegacy</h1>
          </div>
          
          <div style="background: #f9f9f9; padding: 30px; border-radius: 0 0 10px 10px; border: 1px solid #e0e0e0;">
            <h2 style="color: #ef4444; margin-top: 0;">⚠️ Subscription Expired</h2>
            
            <p>Hello ${e(userName)},</p>
            
            <p>Your BitsLegacy subscription has <strong>expired</strong>.</p>
            
            <div style="background: white; padding: 20px; border-radius: 5px; border-left: 4px solid #ef4444; margin: 20px 0;">
              <p style="margin: 5px 0;"><strong>Plan:</strong> ${e(planName)}</p>
              <p style="margin: 5px 0;"><strong>Status:</strong> <span style="color: #ef4444; font-weight: bold;">Expired</span></p>
            </div>
            
            <p><strong>Important:</strong> To continue using BitsLegacy, please renew your subscription. You will not be able to create new vaults until your subscription is renewed.</p>
            
            <div style="text-align: center; margin: 30px 0;">
              <a href="${e(appUrl)}/dashboard/subscribe" style="display: inline-block; background: #ef4444; color: white; padding: 12px 30px; text-decoration: none; border-radius: 5px; font-weight: bold;">Renew Subscription</a>
            </div>
          </div>
        </body>
      </html>
    `

  return { subject, text, html }
}

/** Formats time remaining like old notify-countdown-reminder API. */
export function formatTimeRemainingForReminder(
  timeRemainingSeconds: number,
): string {
  const hours = Math.floor(timeRemainingSeconds / 3600)
  const minutes = Math.floor((timeRemainingSeconds % 3600) / 60)
  return hours > 0
    ? `${hours} hour${hours > 1 ? 's' : ''} and ${minutes} minute${minutes > 1 ? 's' : ''}`
    : `${minutes} minutes`
}
